import { Connection, PublicKey } from '@solana/web3.js';
import { 
  getAssociatedTokenAddress, 
  getAccount 
} from '@solana/spl-token';
import { firebaseService } from './firebase.js';

// Bridge service for handling cross-chain transfers with Firebase admin approval
export class BridgeService {
  constructor() {
    console.log('BridgeService constructor called');
    
    // Determine RPC URL based on mode
    const rpcMode = process.env.REACT_APP_RPC_MODE || 'devnet';
    let rpcUrl = process.env.REACT_APP_RPC_URL;
    
    if (!rpcUrl) {
      // Fallback to appropriate default based on mode
      rpcUrl = rpcMode === 'mainnet' 
        ? 'https://api.mainnet-beta.solana.com'
        : 'https://api.devnet.solana.com';
    }
    
    console.log(`Initializing Solana connection to ${rpcMode} at: ${rpcUrl}`);
    
    this.solanaConnection = new Connection(rpcUrl, 'confirmed');
    
    // DBN token mint address on Solana
    this.solanaTokenMint = new PublicKey(
      process.env.REACT_APP_SPL_TOKEN_MINT_ADDRESS || '11111111111111111111111111111112'
    );
    
    // Minimum confirmations required for BSC transactions
    this.minConfirmations = 12;
    
    // Bridge fee (percentage)
    this.bridgeFee = 0.001; // 0.1%
  }

  /**
   * Submit bridge request for admin approval (save to Firebase)
   */
  async submitBridgeRequest(bridgeData) {
    try {
      const { direction } = bridgeData;
      
      if (direction === 'bsc-to-solana') {
        return await this.submitBSCToSolanaRequest(bridgeData);
      } else if (direction === 'solana-to-bsc') {
        return await this.submitSolanaToBSCRequest(bridgeData);
      } else {
        throw new Error('Invalid bridge direction');
      }
      
    } catch (error) {
      console.error('Bridge request submission failed:', error);
      throw error;
    }
  }

  /**
   * Submit BSC to Solana bridge request
   */
  async submitBSCToSolanaRequest(bridgeData) {
    const { bscTxHash, fromAddress, toSolanaAddress, amount, chainId } = bridgeData;
    
    // Step 1: Verify BSC transaction
    const isValid = await this.verifyBSCTransaction(bscTxHash, fromAddress, amount, chainId);
    if (!isValid) {
      throw new Error('BSC transaction verification failed');
    }
    
    // Step 2: Check if already submitted
    const existingRequest = await firebaseService.getBridgeRequestByTxHash(bscTxHash);
    if (existingRequest) {
      throw new Error('Bridge request already submitted for this transaction');
    }
    
    // Step 3: Save request to Firebase for admin approval
    const bridgeRequest = await firebaseService.saveBridgeRequest({
      direction: 'bsc-to-solana',
      bscTxHash,
      fromAddress,
      toSolanaAddress,
      amount,
      chainId
    });
    
    return {
      success: true,
      message: 'BSC to Solana bridge request submitted successfully! Admin will review and process your request.',
      requestId: bridgeRequest.id,
      status: 'pending',
      estimatedAmount: parseFloat(amount) * 0.999, // Amount after 0.1% fee
      bridgeFee: parseFloat(amount) * 0.001
    };
  }

  /**
   * Submit Solana to BSC bridge request
   */
  async submitSolanaToBSCRequest(bridgeData) {
    const { solanaSignature, fromSolanaAddress, toBSCAddress, amount } = bridgeData;
    
    // Step 1: Verify Solana transaction (TODO: implement)
    // For now, we'll skip verification as it's simulated
    console.log('Verifying Solana transaction:', solanaSignature);
    
    // Step 2: Check if already submitted
    const existingRequest = await firebaseService.getBridgeRequestByTxHash(solanaSignature);
    if (existingRequest) {
      throw new Error('Bridge request already submitted for this transaction');
    }
    
    // Step 3: Save request to Firebase for admin approval
    const bridgeRequest = await firebaseService.saveBridgeRequest({
      direction: 'solana-to-bsc',
      solanaSignature,
      fromSolanaAddress,
      toBSCAddress,
      amount
    });
    
    return {
      success: true,
      message: 'Solana to BSC bridge request submitted successfully! Admin will review and process your request.',
      requestId: bridgeRequest.id,
      status: 'pending',
      estimatedAmount: parseFloat(amount) * 0.999, // Amount after 0.1% fee
      bridgeFee: parseFloat(amount) * 0.001
    };
  }

  /**
   * Process bridge request from BSC to Solana (DEPRECATED - now manual admin approval)
   */
  async processBridgeRequest(bridgeData) {
    console.warn('processBridgeRequest is deprecated. Use submitBridgeRequest instead.');
    return this.submitBridgeRequest(bridgeData);
  }

  /**
   * Get bridge request status by transaction hash
   */
  async getBridgeRequestStatus(bscTxHash) {
    return await firebaseService.getBridgeRequestByTxHash(bscTxHash);
  }

  /**
   * Get user's bridge requests
   */
  async getUserBridgeRequests(userAddress) {
    return await firebaseService.getBridgeRequestsByUser(userAddress);
  }

  /**
   * Get all bridge requests (for admin)
   */
  async getAllBridgeRequests() {
    return await firebaseService.getAllBridgeRequests();
  }

  /**
   * Get bridge statistics (for admin dashboard)
   */
  async getBridgeStatistics() {
    return await firebaseService.getStatistics();
  }

  /**
   * Admin method: Approve bridge request
   */
  async adminApproveBridgeRequest(requestId, adminNotes = '') {
    try {
      const requests = await firebaseService.getAllBridgeRequests();
      const request = requests.find(r => r.id === requestId);
      
      if (!request) {
        throw new Error('Bridge request not found');
      }
      
      if (request.status !== 'pending') {
        throw new Error('Request is not in pending status');
      }
      
      // Update status to approved
      await firebaseService.updateBridgeRequestStatus(requestId, 'approved', adminNotes);
      
      return {
        success: true,
        message: 'Bridge request approved. Please send tokens manually and mark as completed.',
        requestId: requestId,
        toAddress: request.toSolanaAddress,
        amount: request.netAmount
      };
      
    } catch (error) {
      console.error('Admin bridge approval failed:', error);
      throw error;
    }
  }

  /**
   * Admin method: Mark bridge request as completed with completion transaction hash
   */
  async adminCompleteBridgeRequest(requestId, completionTxHash, adminNotes = '') {
    try {
      await firebaseService.updateBridgeRequestStatus(requestId, 'completed', adminNotes, completionTxHash);
      
      return {
        success: true,
        message: 'Bridge request marked as completed',
        requestId: requestId,
        completionTxHash: completionTxHash
      };
      
    } catch (error) {
      console.error('Admin bridge completion failed:', error);
      throw error;
    }
  }

  /**
   * Admin method: Reject bridge request
   */
  async adminRejectBridgeRequest(requestId, reason) {
    try {
      await firebaseService.updateBridgeRequestStatus(requestId, 'rejected', reason);
      
      return {
        success: true,
        message: 'Bridge request rejected',
        requestId: requestId,
        reason: reason
      };
      
    } catch (error) {
      console.error('Admin bridge rejection failed:', error);
      throw error;
    }
  }

  /**
   * Verify BSC transaction
   */
  async verifyBSCTransaction(txHash, fromAddress, amount, chainId) {
    try {
      // This is a placeholder verification
      // In production, you would:
      // 1. Connect to BSC RPC
      // 2. Get transaction details
      // 3. Verify it's a token transfer to the bridge contract
      // 4. Verify the amount and sender
      // 5. Check confirmation count
      
      console.log('Verifying BSC transaction:', {
        txHash,
        fromAddress,
        amount,
        chainId
      });
      
      // For demo purposes, basic validation
      if (!txHash || !fromAddress || !amount || !chainId) {
        return false;
      }
      
      // Check if transaction hash format is valid
      if (!/^0x[a-fA-F0-9]{64}$/.test(txHash)) {
        return false;
      }
      
      // Check if address format is valid
      if (!/^0x[a-fA-F0-9]{40}$/.test(fromAddress)) {
        return false;
      }
      
      // Check if amount is positive
      if (parseFloat(amount) <= 0) {
        return false;
      }
      
      // In production, add actual BSC transaction verification here
      return true;
      
    } catch (error) {
      console.error('BSC transaction verification error:', error);
      return false;
    }
  }

  /**
   * Check if transaction was already processed
   */
  async isTransactionProcessed(bscTxHash) {
    try {
      const existingRequest = await firebaseService.getBridgeRequestByTxHash(bscTxHash);
      return existingRequest && existingRequest.status === 'completed';
    } catch (error) {
      console.error('Error checking transaction status:', error);
      return false;
    }
  }

  /**
   * Get bridge history for a user
   */
  async getBridgeHistory(address) {
    try {
      return this.getUserBridgeRequests(address);
    } catch (error) {
      console.error('Error fetching bridge history:', error);
      return [];
    }
  }

  /**
   * Get bridge statistics
   */
  async getBridgeStats() {
    try {
      return await this.getBridgeStatistics();
    } catch (error) {
      console.error('Error fetching bridge stats:', error);
      return {
        total: 0,
        pending: 0,
        approved: 0,
        completed: 0,
        rejected: 0,
        totalVolume: 0,
        totalFees: 0
      };
    }
  }

  /**
   * Get Solana token balance for a wallet
   */
  async getSolanaTokenBalance(walletAddress, tokenMintAddress) {
    try {
      const walletPublicKey = new PublicKey(walletAddress);
      const mintPublicKey = new PublicKey(tokenMintAddress);
      
      console.log(`Fetching token balance for wallet: ${walletAddress}`);
      console.log(`Token mint: ${tokenMintAddress}`);
      console.log(`Using RPC: ${this.solanaConnection.rpcEndpoint}`);
      
      // Get the associated token account address
      const associatedTokenAddress = await getAssociatedTokenAddress(
        mintPublicKey,
        walletPublicKey
      );
      
      console.log(`Associated token account: ${associatedTokenAddress.toString()}`);
      
      try {
        // Test connection first
        const slot = await this.solanaConnection.getSlot();
        console.log(`RPC connection successful, current slot: ${slot}`);
        
        // Get account info
        const tokenAccountInfo = await getAccount(
          this.solanaConnection,
          associatedTokenAddress
        );
        
        // Convert balance from lamports to tokens (assuming 9 decimals for DBN)
        const balance = Number(tokenAccountInfo.amount) / Math.pow(10, 9);
        console.log(`Token balance found: ${balance}`);
        return balance.toFixed(4);
        
      } catch (accountError) {
        // Check if it's a 403 error or token account doesn't exist
        if (accountError.message && accountError.message.includes('403')) {
          console.error('RPC Access forbidden (403) - Check your RPC endpoint and API key');
          console.error('Current RPC endpoint:', this.solanaConnection.rpcEndpoint);
          throw new Error('RPC access forbidden. Please check your Solana RPC configuration.');
        }
        
        // Account doesn't exist or has no tokens
        console.log('Token account not found or empty:', accountError.message);
        return '0.0000';
      }
      
    } catch (error) {
      console.error('Error fetching Solana token balance:', error);
      
      if (error.message && error.message.includes('403')) {
        throw new Error('RPC access forbidden. Please check your Solana RPC configuration.');
      }
      
      throw error;
    }
  }
}

// Export singleton instance
console.log('Creating bridgeService instance...');
export const bridgeService = new BridgeService();
console.log('BridgeService instance created:', bridgeService);
export default bridgeService;
